import React, {useState} from 'react';
import {
    Box, Button, Card, CardActions, CardContent, Checkbox, CircularProgress, FormControlLabel, Link, Stack, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {ApplyProposalRequest, NodeChangeProposal, ProposalField} from '../../../../../@types/agent';
import {ResourceType} from '../../../../../@types/resource-type';
import {useWorkspaceContext} from '../../../../../context/workspaceContext';
import agentService from '../../../../../services/agent.service';
import EventSequence from '../../../../../@types/event-sequence';

export type ProposalState = 'pending' | 'applied' | 'rejected' | 'stale' | 'error';

/** What the turn hands the widget: its proposals and what has already become of them. */
export interface ProposedChangesPayload {
    sessionId: string;
    proposals: NodeChangeProposal[];
    decided?: Record<string, ProposalState>;
    onDecided?: (decisions: Record<string, ProposalState>) => void;
}

const FIELD_LABEL: Record<ProposalField, string> = {
    statementOfTruth: 'Statement of truth',
    name: 'Name',
    description: 'Description',
    nodeClass: 'Node class',
    type: 'Gate type',
};

const STATE_LABEL: Record<ProposalState, string> = {
    pending: '',
    applied: 'Applied',
    rejected: 'Dismissed',
    stale: 'The model has changed since this was suggested. Ask again for a fresh suggestion.',
    error: 'Could not apply',
};

interface FailedRequest {
    response?: {status?: number; data?: {message?: string}};
    message?: string;
}

const isAddition = (proposal: NodeChangeProposal): boolean =>
    proposal.kind === 'ADD_CATCH_ALL_CHILD' || proposal.kind === 'ADD_CHILD';

/** The new node's class and gate, as far as the proposal gives them. */
const newNodeDetail = (proposal: NodeChangeProposal): string => {
    const parts = [proposal.nodeClass, proposal.gateType ? `${proposal.gateType} gate` : null]
        .filter((part): part is string => Boolean(part));
    return parts.length > 0 ? ` (${parts.join(', ')})` : '';
};

/** What the change is, in the few words that go above the detail. */
const headlineFor = (proposal: NodeChangeProposal): string => {
    if (proposal.kind === 'ADD_CHILD') {
        return `Add new node: "${proposal.newValue}"${newNodeDetail(proposal)}`;
    }
    return isAddition(proposal)
        ? `Add child: "${proposal.newValue}"`
        : FIELD_LABEL[proposal.field as ProposalField] ?? String(proposal.field);
};

/** Whether a proposal is still the analyst's to decide. */
const isOpen = (state: ProposalState): boolean => state === 'pending' || state === 'error';

/**
 * What a settled change shows where its checkbox was.
 *
 * <p>Emptying the box and leaving a line of small print underneath read as nothing having happened,
 * which is the opposite of what had happened. The mark carries the outcome, and the text beside it
 * says the same thing in words for anyone who cannot use the colour.</p>
 */
const OUTCOME_MARK: Record<Exclude<ProposalState, 'pending' | 'error'>, React.ReactElement> = {
    applied: <CheckCircleIcon fontSize="small" color="success" titleAccess="Applied" />,
    rejected: <RemoveCircleOutlineIcon fontSize="small" color="disabled" titleAccess="Dismissed" />,
    stale: <ErrorOutlineIcon fontSize="small" color="warning" titleAccess="Out of date" />,
};

/** What is sent to apply one proposal, which the two kinds fill differently. */
const applyRequestFor = (sessionId: string, proposal: NodeChangeProposal): ApplyProposalRequest => ({
    sessionId,
    proposalId: proposal.proposalId,
    kind: proposal.kind ?? 'EDIT_FIELD',
    nodeId: proposal.nodeId,
    field: proposal.field,
    oldValue: proposal.oldValue,
    newValue: proposal.newValue,
    statement: proposal.statement ?? null,
    reason: proposal.reason,
    proposedAt: proposal.proposedAt,
    nodeClass: proposal.nodeClass ?? null,
    gateType: proposal.gateType ?? null,
});

/** How one applied proposal turned out. */
const outcomeOf = (error: unknown): ProposalState => {
    const failed = error as FailedRequest;
    return failed.response?.status === 409 ? 'stale' : 'error';
};

const detailOf = (error: unknown): string => {
    const failed = error as FailedRequest;
    return failed.response?.data?.message ?? failed.message ?? 'unknown error';
};

interface OneChangeProps {
    proposal: NodeChangeProposal;
    state: ProposalState;
    detail?: string;
    ticked: boolean;
    disabled: boolean;
    onTick: (ticked: boolean) => void;
}

/** One suggested change: what it would do, to which node, and why. */
const OneChange = ({proposal, state, detail, ticked, disabled, onTick}: OneChangeProps): React.ReactElement => {
    const {canvasSearchFunction, canvasNavigateFunction} = useWorkspaceContext();

    const navigate = (): void => {
        if (!canvasNavigateFunction) {
            return;
        }
        canvasNavigateFunction(`ft-${proposal.nodeId}`);
        const results = canvasSearchFunction ? canvasSearchFunction(proposal.nodeName) : [];
        if (results.length > 0 && results[0].id !== `ft-${proposal.nodeId}`) {
            canvasNavigateFunction(results[0].id);
        }
    };

    return (
        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.75}}>
            {isOpen(state) ? (
                <Checkbox
                    size="small"
                    checked={ticked}
                    disabled={disabled}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => onTick(event.target.checked)}
                    inputProps={{'aria-label': `${headlineFor(proposal)} on ${proposal.label ?? proposal.uniqueId} ${proposal.nodeName}`}}
                    sx={{p: 0.5, mt: -0.5}}
                />
            ) : (
                <Box sx={{p: 0.5, mt: -0.5, display: 'flex', alignItems: 'center', height: 30, width: 30,
                    justifyContent: 'center'}}>
                    {OUTCOME_MARK[state as Exclude<ProposalState, 'pending' | 'error'>]}
                </Box>
            )}
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography variant="subtitle2" component="div">
                    <Link component="button" type="button" underline="hover" onClick={navigate}>
                        {proposal.label ?? proposal.uniqueId} {proposal.nodeName}
                    </Link>
                </Typography>
                <Typography variant="caption" color="text.secondary">{headlineFor(proposal)}</Typography>
                {proposal.oldValue && (
                    <Typography variant="body2" sx={{textDecoration: 'line-through', color: 'text.secondary'}}>
                        {proposal.oldValue}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    sx={state === 'applied' ? {color: 'success.main'} : undefined}
                >
                    {isAddition(proposal) ? proposal.statement : proposal.newValue}
                </Typography>
                <Typography variant="caption" sx={{display: 'block', mt: 0.5}}>{proposal.reason}</Typography>
                {state !== 'pending' && (
                    <Typography
                        variant="caption"
                        fontWeight={state === 'applied' ? 600 : undefined}
                        color={state === 'applied' ? 'success.main' : 'text.secondary'}
                        sx={{display: 'block', mt: 0.5}}
                    >
                        {STATE_LABEL[state]}{detail ? `: ${detail}` : ''}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

/**
 * The changes one turn suggested, as a list the analyst works through.
 *
 * <p>Nothing is ticked to begin with. These are a model's suggestions about a safety model, so each
 * one is opted into deliberately rather than opted out of, and the analyst reads the old value
 * against the new before deciding.</p>
 *
 * <p>Applying goes one at a time on purpose. Each change writes a version of the view and hands back
 * the whole view, so running them together would race on the version and on which answer the canvas
 * ends up showing. Going in order also means a failure part way through leaves the ones before it
 * applied and says so, rather than leaving the analyst guessing.</p>
 */
export default function ProposedChanges({payload}: {payload: ProposedChangesPayload}): React.ReactElement {
    const {setCurrentWorkspaceObject} = useWorkspaceContext();
    const [states, setStates] = useState<Record<string, ProposalState>>(payload.decided ?? {});
    const [details, setDetails] = useState<Record<string, string>>({});
    const [ticked, setTicked] = useState<Record<string, boolean>>({});
    const [busy, setBusy] = useState<boolean>(false);

    const stateOf = (proposal: NodeChangeProposal): ProposalState => states[proposal.proposalId] ?? 'pending';
    const open = payload.proposals.filter((proposal) => isOpen(stateOf(proposal)));
    const chosen = open.filter((proposal) => ticked[proposal.proposalId]);

    const record = (decided: Record<string, ProposalState>, detailed: Record<string, string>): void => {
        setStates((held) => ({...held, ...decided}));
        setDetails((held) => ({...held, ...detailed}));
        // An error is not a decision: the analyst can tick it again, so it is not reported as one.
        const keep = Object.fromEntries(Object.entries(decided).filter(([, state]) => state !== 'error'));
        if (payload.onDecided && Object.keys(keep).length > 0) {
            payload.onDecided(keep);
        }
    };

    const applySelected = async (): Promise<void> => {
        setBusy(true);
        const decided: Record<string, ProposalState> = {};
        const detailed: Record<string, string> = {};
        let view: EventSequence | null = null;
        for (const proposal of chosen) {
            try {
                // eslint-disable-next-line no-await-in-loop
                view = await agentService.applyProposal(applyRequestFor(payload.sessionId, proposal));
                decided[proposal.proposalId] = 'applied';
            } catch (error) {
                decided[proposal.proposalId] = outcomeOf(error);
                detailed[proposal.proposalId] = detailOf(error);
            }
        }
        // The canvas is refreshed once, from the last view that came back, so it is not redrawn
        // between changes only to be redrawn again.
        if (view) {
            setCurrentWorkspaceObject({objectType: ResourceType.EVENT_SEQUENCE_TREES, object: view, redrawCanvas: true});
        }
        setTicked({});
        record(decided, detailed);
        setBusy(false);
    };

    const dismissRest = async (): Promise<void> => {
        setBusy(true);
        const decided: Record<string, ProposalState> = {};
        for (const proposal of open) {
            decided[proposal.proposalId] = 'rejected';
            try {
                // eslint-disable-next-line no-await-in-loop
                await agentService.recordRejection({
                    sessionId: payload.sessionId,
                    proposalId: proposal.proposalId,
                    kind: proposal.kind ?? 'EDIT_FIELD',
                    nodeId: proposal.nodeId,
                    field: proposal.field,
                    decision: 'REJECTED',
                    proposedAt: proposal.proposedAt,
                });
            } catch {
                // A dismissal is a dismissal even if the audit call failed; the analyst's view must not change.
            }
        }
        setTicked({});
        record(decided, {});
        setBusy(false);
    };

    const allTicked = open.length > 0 && chosen.length === open.length;

    return (
        <Card variant="outlined" sx={{mt: 1}}>
            <CardContent sx={{pb: 0}}>
                <Typography variant="subtitle2" gutterBottom>
                    {`Suggested changes (${payload.proposals.length})`}
                </Typography>
                {open.length > 1 && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={allTicked}
                                indeterminate={chosen.length > 0 && !allTicked}
                                disabled={busy}
                                onChange={(): void => setTicked(allTicked
                                    ? {}
                                    : Object.fromEntries(open.map((proposal) => [proposal.proposalId, true])))}
                                sx={{p: 0.5}}
                            />
                        }
                        label={<Typography variant="caption">Select all</Typography>}
                    />
                )}
                <Stack divider={<Box sx={{borderTop: 1, borderColor: 'divider'}} />}>
                    {payload.proposals.map((proposal) => (
                        <OneChange
                            key={proposal.proposalId}
                            proposal={proposal}
                            state={stateOf(proposal)}
                            detail={details[proposal.proposalId]}
                            ticked={Boolean(ticked[proposal.proposalId])}
                            disabled={busy}
                            onTick={(next: boolean): void =>
                                setTicked((held) => ({...held, [proposal.proposalId]: next}))}
                        />
                    ))}
                </Stack>
            </CardContent>
            {open.length > 0 && (
                <CardActions>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={busy || chosen.length === 0}
                        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
                        onClick={(): void => {
                            void applySelected();
                        }}
                    >
                        {chosen.length > 0 ? `Apply ${chosen.length} selected` : 'Apply selected'}
                    </Button>
                    <Button
                        size="small"
                        disabled={busy}
                        onClick={(): void => {
                            void dismissRest();
                        }}
                    >
                        {chosen.length > 0 && chosen.length < open.length ? 'Dismiss the rest' : 'Dismiss'}
                    </Button>
                </CardActions>
            )}
        </Card>
    );
}
